import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Stack,
  TextField,
  InputAdornment,
  MenuItem,
  CircularProgress,
  Alert,
  Tooltip,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Snackbar,
  Skeleton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import VideocamIcon from '@mui/icons-material/Videocam';
import SyncIcon from '@mui/icons-material/Sync';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PlaceIcon from '@mui/icons-material/Place';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ReplayIcon from '@mui/icons-material/Replay';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import SportsHockeyIcon from '@mui/icons-material/SportsHockey';
import TerminalIcon from '@mui/icons-material/Terminal';

import { auth } from '../../firebase';
import { useSeason } from '../../SeasonContext';
import { useRoster } from '../../RosterContext';
import {
  fetchGameVideos,
  syncScheduleVideos,
  fetchIngestionStatus,
  scheduleGameDownload,
  retryGameDownload,
} from '../../utils/apiClient';
import { GameVideoSummary, GameIngestionItem, IngestionStatusResponse, Game } from '../../types';
import { fetchAndProcessData } from '../../utils/hockeyStatsParser';
import { resolveGameScore } from '../../utils/gameScoreUtils';
import { TaskDebugLogsModal } from './TaskDebugLogsModal';

const GameHighlightsListPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedSeason } = useSeason();
  const { isAdmin, isAuthReady, userProfile } = useRoster();
  const userEmail = (userProfile?.email || auth.currentUser?.email || '').trim().toLowerCase();
  const isSuperAdmin = userEmail === 'bwuest@gmail.com';
  const isUserAdmin = isSuperAdmin || Boolean(isAdmin);
  const canAccess = Boolean(userEmail || isAuthReady);

  // Active Tab: 0 = Downloads, 1 = Queued, 2 = Tasks (Admins only)
  const [activeTab, setActiveTab] = useState<number>(0);

  // Library State (Downloaded videos)
  const [videos, setVideos] = useState<GameVideoSummary[]>([]);
  const [scheduleGames, setScheduleGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [syncing, setSyncing] = useState<boolean>(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  // Queued Tab State
  const [queuedSearch, setQueuedSearch] = useState<string>('');

  // Admin Ingestion Monitor State
  const [ingestionData, setIngestionData] = useState<IngestionStatusResponse | null>(null);
  const [ingestionLoading, setIngestionLoading] = useState<boolean>(false);
  const [ingestionFilter, setIngestionFilter] = useState<string>('all');
  const [ingestionSort, setIngestionSort] = useState<'queue' | 'newest' | 'oldest'>('queue');
  const [ingestionSearch, setIngestionSearch] = useState<string>('');
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [selectedLogGame, setSelectedLogGame] = useState<{ gameId: string; opponent?: string; date?: string } | null>(null);
  const [actionToast, setActionToast] = useState<{ message: string; severity: 'success' | 'info' | 'warning' | 'error' } | null>(null);

  const loadVideos = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchGameVideos(selectedSeason?.key);
      setVideos(Array.isArray(data) ? data : ((data as any)?.videos || []));
    } catch (err: any) {
      setError(err.message || 'Failed to load game videos.');
    } finally {
      setLoading(false);
    }
  };

  const loadIngestionStatus = async () => {
    setIngestionLoading(true);
    try {
      const data = await fetchIngestionStatus(selectedSeason?.key);
      setIngestionData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load ingestion status.');
    } finally {
      setIngestionLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthReady && canAccess) {
      loadVideos();
      if (isUserAdmin) {
        loadIngestionStatus();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeason?.key, canAccess, isAuthReady, isUserAdmin]);

  // Load official league schedule games for final scores & outcome resolution
  useEffect(() => {
    if (!selectedSeason?.seasonId || !selectedSeason?.divisionId) return;
    let isMounted = true;
    fetchAndProcessData(selectedSeason.seasonId, selectedSeason.divisionId)
      .then((data) => {
        if (isMounted && data?.fullSchedule) {
          setScheduleGames(data.fullSchedule);
        }
      })
      .catch((err) => {
        console.warn('Failed to load schedule games for highlights score matching:', err);
      });

    return () => {
      isMounted = false;
    };
  }, [selectedSeason?.seasonId, selectedSeason?.divisionId]);

  // Auto-refresh ingestion tasks when a worker is actively processing
  useEffect(() => {
    const hasActiveTask = ingestionData?.games?.some(
      (g) => g.status === 'processing' || g.lastTaskStatus === 'running'
    );
    if (!hasActiveTask || !isUserAdmin) return;

    const interval = setInterval(() => {
      loadIngestionStatus();
    }, 6000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingestionData?.games, isUserAdmin]);

  const handleSyncSchedule = async () => {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const res = await syncScheduleVideos();
      const msg = `Synced schedule: ${res.queuedGamesCount} game(s) queued for automated download.`;
      setSyncMessage(msg);
      setActionToast({ message: msg, severity: 'success' });
      await Promise.all([loadVideos(), loadIngestionStatus()]);
    } catch (err: any) {
      const msg = err.message || 'Failed to sync schedule.';
      setError(msg);
      setActionToast({ message: msg, severity: 'error' });
    } finally {
      setSyncing(false);
    }
  };

  const handleScheduleGame = async (game: GameIngestionItem) => {
    setActionInProgress(game.gameId);
    try {
      const res = await scheduleGameDownload(game.gameId, {
        opponent: game.opponent,
        rinkName: game.rinkName,
        surfaceId: game.surfaceId,
      });
      const timeStr = res?.scheduledFor ? new Date(res.scheduledFor).toLocaleTimeString() : 'in 1m';
      const msg = `Scheduled download task for game vs ${game.opponent} (run time: ${timeStr}).`;
      setSyncMessage(msg);
      setActionToast({ message: msg, severity: 'success' });
      await loadIngestionStatus();
    } catch (err: any) {
      const msg = err.message || 'Failed to schedule game download.';
      setError(msg);
      setActionToast({ message: msg, severity: 'error' });
    } finally {
      setActionInProgress(null);
    }
  };

  const handleRetryFeed = async (gameId: string, feedType: string = 'pano') => {
    setActionInProgress(gameId);
    try {
      const res = await retryGameDownload(gameId, feedType);
      const timeStr = res?.scheduledFor ? new Date(res.scheduledFor).toLocaleTimeString() : 'in 1m';
      const msg = `Dispatched ${feedType.toUpperCase()} retry! Scheduled execution: ${timeStr} (Task: ${res?.taskName || 'queued'}).`;
      setSyncMessage(msg);
      setActionToast({ message: msg, severity: 'success' });
      await loadIngestionStatus();
    } catch (err: any) {
      const msg = err.message || 'Failed to retry feed.';
      setError(msg);
      setActionToast({ message: msg, severity: 'error' });
    } finally {
      setActionInProgress(null);
    }
  };

  // Filtered Downloaded Replays (Ready to watch)
  const downloadedVideos = useMemo(() => videos.filter((v) => {
    const hasAuto = v.feeds?.auto?.status === 'downloaded';
    const hasPano = v.feeds?.pano?.status === 'downloaded';
    return hasAuto || hasPano || v.status === 'complete' || v.status === 'partial' || (v as any).status === 'ready';
  }), [videos]);

  const filteredDownloadedVideos = useMemo(() => {
    const q = searchQuery.toLowerCase();
    const list = downloadedVideos.filter((v) => {
      return (
        v.opponent.toLowerCase().includes(q) ||
        v.rinkName.toLowerCase().includes(q) ||
        v.date.includes(q)
      );
    });

    return [...list].sort((a, b) => {
      const dateA = `${a.date || ''} ${a.scheduledStart || ''}`;
      const dateB = `${b.date || ''} ${b.scheduledStart || ''}`;
      return sortOrder === 'newest'
        ? dateB.localeCompare(dateA)
        : dateA.localeCompare(dateB);
    });
  }, [downloadedVideos, searchQuery, sortOrder]);

  // Filtered Queued Downloads (Pending/scheduled games)
  // Sorted by: active/downloading first -> next to download earliest -> furthest out in future last
  const queuedGames = useMemo(() => {
    return (ingestionData?.games || [])
      .filter((g) => {
        return (
          g.status === 'scheduled' ||
          g.status === 'overrun_delayed' ||
          g.status === 'retrying_pano' ||
          g.status === 'retrying_auto' ||
          g.status === 'processing' ||
          (g.status === 'partial' && Boolean(g.scheduledExecutionTime))
        );
      })
      .sort((a, b) => {
        // 1. Actively downloading ('processing') always on top
        if (a.status === 'processing' && b.status !== 'processing') return -1;
        if (b.status === 'processing' && a.status !== 'processing') return 1;

        // 2. Sort by scheduledExecutionTime ascending (soonest / next to run first)
        const timeA = a.scheduledExecutionTime ? new Date(a.scheduledExecutionTime).getTime() : 0;
        const timeB = b.scheduledExecutionTime ? new Date(b.scheduledExecutionTime).getTime() : 0;

        if (timeA && timeB) {
          return timeA - timeB;
        }
        if (timeA && !timeB) return -1;
        if (!timeA && timeB) return 1;

        // 3. Fallback: game date & time ascending
        const dateA = `${a.date || ''} ${a.scheduledStart || ''}`;
        const dateB = `${b.date || ''} ${b.scheduledStart || ''}`;
        return dateA.localeCompare(dateB);
      });
  }, [ingestionData?.games]);

  const filteredQueuedGames = useMemo(() => {
    const q = queuedSearch.toLowerCase();
    return queuedGames.filter((g) => {
      return (
        g.opponent.toLowerCase().includes(q) ||
        g.rinkName.toLowerCase().includes(q) ||
        g.date.includes(q)
      );
    });
  }, [queuedGames, queuedSearch]);

  // Filtered Ingestion Items (Tasks monitor tab)
  const filteredIngestionGames = useMemo(() => {
    const list = (ingestionData?.games || []).filter((g) => {
      const matchesSearch =
        g.opponent.toLowerCase().includes(ingestionSearch.toLowerCase()) ||
        g.rinkName.toLowerCase().includes(ingestionSearch.toLowerCase()) ||
        g.date.includes(ingestionSearch);

      if (!matchesSearch) return false;
      if (ingestionFilter === 'all') return true;
      if (ingestionFilter === 'scheduled') return g.status === 'scheduled';
      if (ingestionFilter === 'ready') return g.status === 'ready' || g.status === 'complete' || g.status === 'partial';
      if (ingestionFilter === 'retrying') return g.status === 'retrying_pano' || g.status === 'retrying_auto' || g.status === 'overrun_delayed';
      if (ingestionFilter === 'no_feed') return g.status === 'no_feed' || !g.hasLivebarn;
      if (ingestionFilter === 'not_scheduled') return g.status === 'not_scheduled';
      return true;
    });

    if (ingestionSort === 'newest') {
      return [...list].sort((a, b) => `${b.date} ${b.scheduledStart || ''}`.localeCompare(`${a.date} ${a.scheduledStart || ''}`));
    }
    if (ingestionSort === 'oldest') {
      return [...list].sort((a, b) => `${a.date} ${a.scheduledStart || ''}`.localeCompare(`${b.date} ${b.scheduledStart || ''}`));
    }

    // Default 'queue' priority sort:
    // Active/queued tasks (processing, retrying, scheduled) come FIRST, ordered by execution time ascending (next to run earliest).
    // Ready/completed games come next, ordered by game date descending (most recently played first).
    // Unscheduled / no feed games come last.
    return [...list].sort((a, b) => {
      const isQueuedA = ['processing', 'retrying_pano', 'retrying_auto', 'overrun_delayed', 'scheduled'].includes(a.status);
      const isQueuedB = ['processing', 'retrying_pano', 'retrying_auto', 'overrun_delayed', 'scheduled'].includes(b.status);

      if (isQueuedA && !isQueuedB) return -1;
      if (!isQueuedA && isQueuedB) return 1;

      if (isQueuedA && isQueuedB) {
        if (a.status === 'processing' && b.status !== 'processing') return -1;
        if (b.status === 'processing' && a.status !== 'processing') return 1;

        const timeA = a.scheduledExecutionTime ? new Date(a.scheduledExecutionTime).getTime() : 0;
        const timeB = b.scheduledExecutionTime ? new Date(b.scheduledExecutionTime).getTime() : 0;
        if (timeA && timeB) return timeA - timeB;
        if (timeA && !timeB) return -1;
        if (!timeA && timeB) return 1;
        return `${a.date} ${a.scheduledStart || ''}`.localeCompare(`${b.date} ${b.scheduledStart || ''}`);
      }

      const isReadyA = ['ready', 'complete', 'partial'].includes(a.status);
      const isReadyB = ['ready', 'complete', 'partial'].includes(b.status);
      if (isReadyA && !isReadyB) return -1;
      if (!isReadyA && isReadyB) return 1;
      if (isReadyA && isReadyB) {
        return `${b.date} ${b.scheduledStart || ''}`.localeCompare(`${a.date} ${a.scheduledStart || ''}`);
      }

      return `${a.date} ${a.scheduledStart || ''}`.localeCompare(`${b.date} ${b.scheduledStart || ''}`);
    });
  }, [ingestionData?.games, ingestionSearch, ingestionFilter, ingestionSort]);

  const summary = ingestionData?.summary;

  if (isAuthReady && !canAccess) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 2, gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <VideocamIcon color="primary" sx={{ fontSize: 36 }} />
            Game Highlights & Video Center
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Watch full game broadcasts, jump to key period transitions, and download goal highlights.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5}>
          {isUserAdmin && (
            <Button
              variant="outlined"
              startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
              onClick={handleSyncSchedule}
              disabled={syncing}
              size="small"
            >
              {syncing ? 'Scanning...' : 'Sync Schedule'}
            </Button>
          )}

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              loadVideos();
              if (isUserAdmin) {
                loadIngestionStatus();
              }
            }}
            size="small"
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      {syncMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSyncMessage(null)}>
          {syncMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs - Only Rendered for Admins */}
      {isUserAdmin && (
        <Paper square elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={(_, newVal) => setActiveTab(newVal)}>
            <Tab
              icon={<VideocamIcon fontSize="small" />}
              iconPosition="start"
              label={`Downloads (${downloadedVideos.length})`}
            />
            <Tab
              icon={<ScheduleSendIcon fontSize="small" />}
              iconPosition="start"
              label={`Queued (${queuedGames.length})`}
            />
            <Tab
              icon={<PendingIcon fontSize="small" />}
              iconPosition="start"
              label={`Tasks (${summary?.totalGames || ingestionData?.games?.length || 0})`}
            />
          </Tabs>
        </Paper>
      )}

      {/* TAB 0: DOWNLOADS (Library of completed video recordings - visible to all) */}
      {(activeTab === 0 || !isUserAdmin) && (
        <Box>
          {/* Filters Bar */}
          <Card variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'background.paper' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 8, md: 9 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search downloaded replays by opponent, rink, or date (YYYY-MM-DD)..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Sort Order"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value as 'newest' | 'oldest')}
                >
                  <MenuItem value="newest">Newest First</MenuItem>
                  <MenuItem value="oldest">Oldest First</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Card>

          {loading || !isAuthReady ? (
            <Grid container spacing={3}>
              {Array.from({ length: 6 }).map((_, idx) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={`video-skeleton-${idx}`}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Skeleton variant="text" width={140} height={20} />
                        <Skeleton variant="rounded" width={80} height={24} sx={{ borderRadius: 16 }} />
                      </Box>

                      <Skeleton variant="text" width="65%" height={32} sx={{ mb: 0.5 }} />
                      <Skeleton variant="text" width="50%" height={20} sx={{ mb: 1.5 }} />

                      <Skeleton variant="rounded" width="100%" height={38} sx={{ borderRadius: 1.5, mb: 1.5 }} />

                      <Stack direction="row" spacing={1} sx={{ mt: 'auto', pt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                        <Skeleton variant="rounded" width={75} height={24} sx={{ borderRadius: 16 }} />
                        <Skeleton variant="rounded" width={75} height={24} sx={{ borderRadius: 16 }} />
                        <Skeleton variant="rounded" width={60} height={24} sx={{ borderRadius: 16 }} />
                      </Stack>
                    </CardContent>

                    <CardActions sx={{ p: 2, pt: 0 }}>
                      <Skeleton variant="rounded" width="100%" height={36} sx={{ borderRadius: 1 }} />
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : filteredDownloadedVideos.length === 0 ? (
            <Card variant="outlined" sx={{ p: 5, textAlign: 'center' }}>
              <SportsHockeyIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="h6" color="text.secondary">
                No game replays downloaded yet.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {isUserAdmin
                  ? 'Switch to the "Queued" tab to view pending games scheduled for download or trigger one manually.'
                  : 'Game replays will appear here once games conclude and broadcasts are downloaded.'}
              </Typography>
            </Card>
          ) : (
            <Grid container spacing={3}>
              {filteredDownloadedVideos.map((video) => {
                const hasAuto = video.feeds?.auto?.status === 'downloaded';
                const hasPano = video.feeds?.pano?.status === 'downloaded';
                const goalsCount = video.goals?.length || video.highlights?.length || 0;
                const scoreResult = resolveGameScore(video, scheduleGames);

                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={video.gameId || video.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4,
                        },
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CalendarMonthIcon sx={{ fontSize: 16 }} />
                            {video.date} • {video.scheduledStart}
                          </Typography>

                          <Chip
                            icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
                            label={hasAuto && hasPano ? 'Full Replay' : hasAuto ? 'Auto Feed' : 'Pano Feed'}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        </Box>

                        <Typography variant="h6" fontWeight={600} gutterBottom sx={{ lineHeight: 1.25 }}>
                          vs {video.opponent}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                          <PlaceIcon sx={{ fontSize: 16 }} />
                          {video.rinkName} {video.sheetName && `(${video.sheetName})`}
                        </Typography>

                        {scoreResult && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1.5,
                              p: 0.75,
                              px: 1.25,
                              borderRadius: 1.5,
                              bgcolor: (theme) =>
                                theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Chip
                              label={scoreResult.outcomeLabel.toUpperCase()}
                              color={
                                scoreResult.outcome === 'W'
                                  ? 'success'
                                  : scoreResult.outcome === 'L'
                                  ? 'error'
                                  : 'warning'
                              }
                              size="small"
                              sx={{
                                fontWeight: 800,
                                fontSize: '0.72rem',
                                height: 22,
                                letterSpacing: 0.5,
                              }}
                            />
                            <Typography variant="body2" fontWeight={700} color="text.primary">
                              Final: {scoreResult.jetsScore} – {scoreResult.oppScore}
                              {scoreResult.isOvertime ? ' (OT)' : ''}
                            </Typography>
                          </Box>
                        )}

                        <Stack direction="row" spacing={1} sx={{ mt: 'auto', pt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                          {hasAuto && <Chip label="1080p Auto" size="small" variant="filled" />}
                          {hasPano && <Chip label="Panoramic" size="small" variant="filled" />}
                          {goalsCount > 0 && (
                            <Chip
                              label={`${goalsCount} Goal${goalsCount > 1 ? 's' : ''}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          )}
                        </Stack>
                      </CardContent>

                      <CardActions sx={{ p: 2, pt: 0, display: 'flex', gap: 1 }}>
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<PlayCircleOutlineIcon />}
                          onClick={() => navigate(`/highlights/${video.gameId || video.id}`)}
                        >
                          Watch Game & Clips
                        </Button>
                        {isUserAdmin && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<TerminalIcon />}
                            onClick={() => setSelectedLogGame({ gameId: video.gameId || video.id, opponent: video.opponent, date: video.date })}
                            sx={{ minWidth: '80px' }}
                          >
                            Logs
                          </Button>
                        )}
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      )}

      {/* TAB 1: QUEUED DOWNLOADS (Admins only) */}
      {isUserAdmin && activeTab === 1 && (
        <Box>
          <Card variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'background.paper' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search queued games by opponent, rink, or date..."
              value={queuedSearch}
              onChange={(e) => setQueuedSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Card>

          {ingestionLoading ? (
            <Grid container spacing={3}>
              {Array.from({ length: 3 }).map((_, idx) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={`queued-skeleton-${idx}`}>
                  <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Skeleton variant="text" width={140} height={20} />
                        <Skeleton variant="rounded" width={80} height={24} sx={{ borderRadius: 16 }} />
                      </Box>
                      <Skeleton variant="text" width="60%" height={32} sx={{ mb: 0.5 }} />
                      <Skeleton variant="text" width="45%" height={20} sx={{ mb: 1.5 }} />
                      <Skeleton variant="rounded" width="100%" height={28} sx={{ borderRadius: 1 }} />
                    </CardContent>
                    <CardActions sx={{ p: 2, pt: 0 }}>
                      <Skeleton variant="rounded" width="100%" height={36} sx={{ borderRadius: 1 }} />
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : filteredQueuedGames.length === 0 ? (
            <Card variant="outlined" sx={{ p: 5, textAlign: 'center' }}>
              <ScheduleSendIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="h6" color="text.secondary">
                No downloads currently in queue.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                All scheduled games have completed or have not been queued yet.
              </Typography>
            </Card>
          ) : (
            <Grid container spacing={3}>
              {filteredQueuedGames.map((game) => {
                const isBusy = actionInProgress === game.gameId;
                const isTaskRunning = game.status === 'processing' || game.lastTaskStatus === 'running';
                const queuedScore = resolveGameScore(game as any, scheduleGames);

                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={game.gameId}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CalendarMonthIcon sx={{ fontSize: 16 }} />
                            {game.date} • {game.scheduledStart}
                          </Typography>

                          {isTaskRunning ? (
                            <Chip
                              icon={<CircularProgress size={12} />}
                              label={game.currentStage ? `Processing (${game.currentStage.replace(/_/g, ' ')})` : 'Downloading'}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ) : (
                            <>
                              {game.status === 'scheduled' && (
                                <Chip
                                  icon={<AccessTimeIcon sx={{ fontSize: '14px !important' }} />}
                                  label="Scheduled"
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                />
                              )}
                              {game.status === 'partial' && (
                                <Chip
                                  icon={<WarningAmberIcon sx={{ fontSize: '14px !important' }} />}
                                  label="Pano Retry Queued"
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                />
                              )}
                              {game.status === 'overrun_delayed' && (
                                <Chip
                                  icon={<PendingIcon sx={{ fontSize: '14px !important' }} />}
                                  label="Overrun Delay"
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                />
                              )}
                              {game.status === 'retrying_pano' && (
                                <Chip
                                  icon={<ReplayIcon sx={{ fontSize: '14px !important' }} />}
                                  label={`Retrying Pano (${game.panoAttempts || 1})`}
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                />
                              )}
                              {game.status === 'retrying_auto' && (
                                <Chip
                                  icon={<ReplayIcon sx={{ fontSize: '14px !important' }} />}
                                  label="Retrying Auto"
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                />
                              )}
                            </>
                          )}
                        </Box>

                        <Typography variant="h6" fontWeight={600} gutterBottom>
                          vs {game.opponent}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                          <PlaceIcon sx={{ fontSize: 16 }} />
                          {game.rinkName} {game.sheetName && `(${game.sheetName})`}
                        </Typography>

                        {queuedScore && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1.5,
                              p: 0.75,
                              px: 1.25,
                              borderRadius: 1.5,
                              bgcolor: (theme) =>
                                theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Chip
                              label={queuedScore.outcomeLabel.toUpperCase()}
                              color={
                                queuedScore.outcome === 'W'
                                  ? 'success'
                                  : queuedScore.outcome === 'L'
                                  ? 'error'
                                  : 'warning'
                              }
                              size="small"
                              sx={{
                                fontWeight: 800,
                                fontSize: '0.72rem',
                                height: 22,
                                letterSpacing: 0.5,
                              }}
                            />
                            <Typography variant="body2" fontWeight={700} color="text.primary">
                              Final: {queuedScore.jetsScore} – {queuedScore.oppScore}
                              {queuedScore.isOvertime ? ' (OT)' : ''}
                            </Typography>
                          </Box>
                        )}

                        {isTaskRunning ? (
                          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'rgba(37, 99, 235, 0.1)', borderRadius: 1, border: '1px solid rgba(37, 99, 235, 0.3)' }}>
                            <Typography variant="caption" color="primary.main" sx={{ display: 'block', fontWeight: 700, textTransform: 'uppercase' }}>
                              ⚡ Active Ingestion Task
                            </Typography>
                            <Typography variant="body2" color="primary.light" fontWeight={600} sx={{ mt: 0.25 }}>
                              Stage: {game.currentStage ? game.currentStage.replace(/_/g, ' ') : 'Running'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Live worker is actively downloading and processing stream from LiveBarn.
                            </Typography>
                          </Box>
                        ) : (
                          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                              {game.status === 'retrying_pano' || game.status === 'partial' ? 'NEXT PANO RETRY' : game.status === 'retrying_auto' ? 'NEXT AUTO RETRY' : game.status === 'overrun_delayed' ? 'OVERRUN RECHECK' : 'SCHEDULED DOWNLOAD'}
                            </Typography>
                            <Typography variant="body2" color="primary.main" fontWeight={500} sx={{ mt: 0.25 }}>
                              ⏰ {game.scheduledForFormatted || (game.scheduledExecutionTime ? new Date(game.scheduledExecutionTime).toLocaleString() : '30m after game end')}
                            </Typography>
                            {(game.status === 'retrying_pano' || game.status === 'partial') && (
                              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                                Retrying panoramic full-ice camera feed from LiveBarn.
                              </Typography>
                            )}
                            {game.status === 'retrying_auto' && (
                              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                                Retrying auto-tracking camera feed from LiveBarn.
                              </Typography>
                            )}
                            {game.status === 'overrun_delayed' && (
                              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                                Waiting for game to conclude / flip to final before downloading.
                              </Typography>
                            )}
                          </Box>
                        )}
                      </CardContent>

                      <CardActions sx={{ p: 2, pt: 0, display: 'flex', gap: 1 }}>
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={
                            isBusy || isTaskRunning ? (
                              <CircularProgress size={16} />
                            ) : game.status === 'partial' || game.status === 'retrying_pano' ? (
                              <ReplayIcon />
                            ) : (
                              <ScheduleSendIcon />
                            )
                          }
                          onClick={() => (game.status === 'partial' || game.status === 'retrying_pano' ? handleRetryFeed(game.gameId, 'pano') : handleScheduleGame(game))}
                          disabled={Boolean(isBusy || isTaskRunning)}
                        >
                          {isBusy ? (
                            'Queueing...'
                          ) : isTaskRunning ? (
                            `In Progress (${game.currentStage ? game.currentStage.replace(/_/g, ' ') : 'processing'})`
                          ) : game.status === 'partial' || game.status === 'retrying_pano' ? (
                            'Retry Pano Now'
                          ) : (
                            'Trigger Download Now'
                          )}
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<TerminalIcon />}
                          onClick={() => setSelectedLogGame({ gameId: game.gameId, opponent: game.opponent, date: game.date })}
                          sx={{ minWidth: '80px' }}
                        >
                          Logs
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      )}

      {/* TAB 2: ADMIN INGESTION & TASK MONITOR */}
      {isUserAdmin && activeTab === 2 && (
        <Box>
          {/* Summary Metric Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Card variant="outlined" sx={{ p: 2, textAlign: 'center', borderTop: '4px solid #2e7d32' }}>
                <Typography variant="h4" fontWeight={700} color="success.main">
                  {summary?.readyCount ?? 0}
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  DOWNLOADED & READY
                </Typography>
              </Card>
            </Grid>

            <Grid size={{ xs: 6, sm: 3 }}>
              <Card variant="outlined" sx={{ p: 2, textAlign: 'center', borderTop: '4px solid #0288d1' }}>
                <Typography variant="h4" fontWeight={700} color="info.main">
                  {summary?.scheduledCount ?? 0}
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  SCHEDULED IN QUEUE
                </Typography>
              </Card>
            </Grid>

            <Grid size={{ xs: 6, sm: 3 }}>
              <Card variant="outlined" sx={{ p: 2, textAlign: 'center', borderTop: '4px solid #ed6c02' }}>
                <Typography variant="h4" fontWeight={700} color="warning.main">
                  {summary?.retryingCount ?? 0}
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  RETRYING PANO (+24H)
                </Typography>
              </Card>
            </Grid>

            <Grid size={{ xs: 6, sm: 3 }}>
              <Card variant="outlined" sx={{ p: 2, textAlign: 'center', borderTop: '4px solid #9e9e9e' }}>
                <Typography variant="h4" fontWeight={700} color="text.secondary">
                  {summary?.noFeedCount ?? 0}
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  NO LIVEBARN AT RINK
                </Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Controls Bar */}
          <Card variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Filter by opponent, rink name, or date..."
                  value={ingestionSearch}
                  onChange={(e) => setIngestionSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Filter Status"
                  value={ingestionFilter}
                  onChange={(e) => setIngestionFilter(e.target.value)}
                >
                  <MenuItem value="all">All Ingestion Items ({ingestionData?.games?.length || 0})</MenuItem>
                  <MenuItem value="scheduled">Scheduled in Queue ({summary?.scheduledCount || 0})</MenuItem>
                  <MenuItem value="ready">Ready ({summary?.readyCount || 0})</MenuItem>
                  <MenuItem value="retrying">Retrying Feed ({summary?.retryingCount || 0})</MenuItem>
                  <MenuItem value="no_feed">No LiveBarn Available ({summary?.noFeedCount || 0})</MenuItem>
                  <MenuItem value="not_scheduled">Not Scheduled ({summary?.notScheduledCount || 0})</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Sort Order"
                  value={ingestionSort}
                  onChange={(e) => setIngestionSort(e.target.value as any)}
                >
                  <MenuItem value="queue">Next to Run (Queue Order)</MenuItem>
                  <MenuItem value="newest">Game Date (Newest First)</MenuItem>
                  <MenuItem value="oldest">Game Date (Oldest First)</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Card>

          {/* Ingestion Table */}
          {ingestionLoading ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead sx={{ bgcolor: 'grey.100' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Game & Opponent</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Rink & Surface</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>LiveBarn Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Ingestion State</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Feeds</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from({ length: 5 }).map((_, idx) => (
                    <TableRow key={`table-skeleton-${idx}`}>
                      <TableCell>
                        <Skeleton variant="text" width="70%" height={20} />
                        <Skeleton variant="text" width="40%" height={16} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width="60%" height={20} />
                        <Skeleton variant="text" width="30%" height={16} />
                      </TableCell>
                      <TableCell><Skeleton variant="rounded" width={80} height={22} /></TableCell>
                      <TableCell><Skeleton variant="rounded" width={90} height={22} /></TableCell>
                      <TableCell><Skeleton variant="text" width="50%" height={20} /></TableCell>
                      <TableCell align="right"><Skeleton variant="rounded" width={70} height={30} sx={{ ml: 'auto' }} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : filteredIngestionGames.length === 0 ? (
            <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No ingestion games matching selected filters.
              </Typography>
            </Card>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead sx={{ bgcolor: 'grey.100' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Game & Opponent</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Rink & Surface</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>LiveBarn Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Ingestion State</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Feeds</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredIngestionGames.map((game) => {
                    const isBusy = actionInProgress === game.gameId;

                    return (
                      <TableRow key={game.gameId} hover>
                        {/* Game & Opponent */}
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            vs {game.opponent}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {game.date} • {game.scheduledStart}
                          </Typography>
                          <Chip
                            label={game.source === 'tournament' ? 'Tournament' : 'CSDHL League'}
                            size="small"
                            sx={{ fontSize: '10px', height: 18, mt: 0.5 }}
                          />
                        </TableCell>

                        {/* Rink & Surface */}
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {game.rinkName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {game.sheetName || 'Main'}
                          </Typography>
                        </TableCell>

                        {/* LiveBarn Status */}
                        <TableCell>
                          {game.hasLivebarn ? (
                            <Tooltip title={game.livebarnUrl || ''}>
                              <Chip
                                label={`Surface #${game.surfaceId}`}
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{ fontSize: '11px' }}
                              />
                            </Tooltip>
                          ) : (
                            <Tooltip title="This venue has no LiveBarn cameras installed.">
                              <Chip
                                icon={<WarningAmberIcon sx={{ fontSize: '13px !important' }} />}
                                label="No LiveBarn Feed"
                                size="small"
                                color="error"
                                variant="outlined"
                                sx={{ fontSize: '11px' }}
                              />
                            </Tooltip>
                          )}
                        </TableCell>

                        {/* Ingestion State */}
                        <TableCell>
                          {game.status === 'ready' && (
                            <Chip
                              icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
                              label="Downloaded & Ready"
                              color="success"
                              size="small"
                            />
                          )}

                          {game.status === 'scheduled' && (
                            <Box>
                              <Chip
                                icon={<AccessTimeIcon sx={{ fontSize: '14px !important' }} />}
                                label="Scheduled in Queue"
                                color="info"
                                size="small"
                              />
                              <Typography variant="caption" color="primary.main" sx={{ display: 'block', mt: 0.5, fontWeight: 500 }}>
                                ⏰ {game.scheduledForFormatted || '30m after game end'}
                              </Typography>
                            </Box>
                          )}

                          {game.status === 'retrying_pano' && (
                            <Box>
                              <Chip
                                icon={<ReplayIcon sx={{ fontSize: '14px !important' }} />}
                                label={`Retrying Pano (Attempt ${game.panoAttempts || 1})`}
                                color="warning"
                                size="small"
                              />
                              {game.panoRetryNextAttempt && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  Next: {game.panoRetryNextAttempt.split('T')[0]}
                                </Typography>
                              )}
                            </Box>
                          )}

                          {game.status === 'retrying_auto' && (
                            <Box>
                              <Chip
                                icon={<ReplayIcon sx={{ fontSize: '14px !important' }} />}
                                label="Retrying Auto"
                                color="warning"
                                size="small"
                              />
                            </Box>
                          )}

                          {game.status === 'overrun_delayed' && (
                            <Chip
                              icon={<PendingIcon sx={{ fontSize: '14px !important' }} />}
                              label="Game Overrun Delayed"
                              color="warning"
                              size="small"
                            />
                          )}

                          {game.status === 'no_feed' && (
                            <Chip
                              label="Venue Not Supported"
                              size="small"
                              variant="outlined"
                              sx={{ color: 'text.disabled' }}
                            />
                          )}

                          {game.status === 'not_scheduled' && (
                            <Chip
                              label="Not Queued"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </TableCell>

                        {/* Feeds */}
                        <TableCell>
                          <Stack direction="row" spacing={0.5}>
                            <Chip
                              label="Auto"
                              size="small"
                              color={game.feeds?.auto === 'downloaded' ? 'success' : game.status === 'retrying_auto' ? 'warning' : 'default'}
                              variant={game.feeds?.auto === 'downloaded' ? 'filled' : 'outlined'}
                              sx={{ fontSize: '10px', height: 20 }}
                            />
                            <Chip
                              label="Pano"
                              size="small"
                              color={game.feeds?.pano === 'downloaded' ? 'success' : game.status === 'retrying_pano' ? 'warning' : 'default'}
                              variant={game.feeds?.pano === 'downloaded' ? 'filled' : 'outlined'}
                              sx={{ fontSize: '10px', height: 20 }}
                            />
                          </Stack>
                        </TableCell>

                        {/* Actions */}
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            {(game.status === 'ready' || game.status === 'partial' || game.status === 'complete') && (
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<PlayCircleOutlineIcon />}
                                onClick={() => navigate(`/highlights/${game.gameId}`)}
                              >
                                Watch
                              </Button>
                            )}

                            {(game.status === 'retrying_pano' || ((game.status === 'partial' || game.status === 'ready' || game.status === 'failed') && game.feeds?.pano !== 'downloaded' && game.hasLivebarn)) && (
                              <Tooltip title="Force immediate retry of panoramic feed from LiveBarn">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="warning"
                                    onClick={() => handleRetryFeed(game.gameId, 'pano')}
                                    disabled={isBusy}
                                  >
                                    {isBusy ? <CircularProgress size={16} /> : <ReplayIcon fontSize="small" />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}

                            {game.status === 'retrying_auto' && (
                              <Tooltip title="Force immediate re-check of auto-tracking feed">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="warning"
                                    onClick={() => handleRetryFeed(game.gameId, 'auto')}
                                    disabled={isBusy}
                                  >
                                    {isBusy ? <CircularProgress size={16} /> : <ReplayIcon fontSize="small" />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}

                            {(game.status === 'not_scheduled' || game.status === 'scheduled') && game.hasLivebarn && (
                              <Tooltip title="Queue LiveBarn download via Cloud Tasks">
                                <span>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={isBusy ? <CircularProgress size={14} /> : <ScheduleSendIcon />}
                                    onClick={() => handleScheduleGame(game)}
                                    disabled={isBusy}
                                  >
                                    {game.status === 'scheduled' ? 'Reschedule' : 'Queue'}
                                  </Button>
                                </span>
                              </Tooltip>
                            )}

                            <Tooltip title="View task execution logs & telemetry">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => setSelectedLogGame({ gameId: game.gameId, opponent: game.opponent, date: game.date })}
                              >
                                <TerminalIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {selectedLogGame && (
        <TaskDebugLogsModal
          isOpen={Boolean(selectedLogGame)}
          onClose={() => setSelectedLogGame(null)}
          gameId={selectedLogGame.gameId}
          opponent={selectedLogGame.opponent}
          date={selectedLogGame.date}
        />
      )}

      {/* Action Toast Feedback */}
      {actionToast && (
        <Snackbar
          open={Boolean(actionToast)}
          autoHideDuration={actionToast.severity === 'info' ? 10000 : 6000}
          onClose={() => setActionToast(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity={actionToast.severity}
            onClose={() => setActionToast(null)}
            variant="filled"
            sx={{ width: '100%', boxShadow: 4, fontWeight: 500 }}
          >
            {actionToast.message}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export default GameHighlightsListPage;
